import React, { useState, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Suspense, ErrorBoundary } from '~components/suspense';

import { useGet } from '~hooks/useQuery';
import { useLayout } from '~hooks/useLayout';
import { isVerified, stringToJSON } from '~helpers/common';
import { UserContext } from '~contexts/user';

import { useSeo } from '~hooks/useSeo';
import { useFavorite } from '~hooks/useFavorite';

import Hero from '~components/hero';
import CardSkeleton from '~components/card/skeleton';

import ResultsItemCustomer from '~containers/customers/list/results-item';
import ResultsItemWorker from '~containers/workers/list/results-item';
import NoResults from './no-results';

import '~components/listing/style.scss';

const Favorites = () => {
  const intl = useIntl();

  useLayout({ type: 'account', displayCities: false });
  useSeo(intl.formatMessage({ id: 'containers.favorites.title', defaultMessage: 'Favorites' }));

  const { user } = useContext(UserContext);
  const userTypeToFetch = user.accountType === 'worker' ? 'customers' : 'workers';
  const { favorites, isLoading: isLoadingFavorites } = useFavorite();

  const [shouldFetch, setShouldFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { data: favoritesData, isLoading: isLoadingData } = useGet(
    {
      name: `${userTypeToFetch}`,
      requiresAuthentication: false,
      params: {
        ids: favorites,
      },
      shouldExecute: shouldFetch,
      callback: () => {
        setShouldFetch(false);
        setIsLoading(false);
      },
    },
    [shouldFetch]
  );

  useEffect(() => {
    if (isLoadingFavorites) {
      return;
    }

    if (!favorites || favorites.length === 0) {
      setIsLoading(false);
      return;
    }

    setShouldFetch(true);
  }, [favorites, isLoadingFavorites]);

  return (
    <div className="c-account__main l-container">
      <Hero.Wrapper>
        <Hero.Title>
          {intl.formatMessage({
            id: 'containers.favorites.headline',
            defaultMessage: 'My favorites',
          })}
        </Hero.Title>
      </Hero.Wrapper>
      <div className="c-matches-list c-matches-list--narrow l-mt2">
        <div className="c-matches-list__results">
          <Suspense fallback={<CardSkeleton skeletonCount={3} />} isLoading={isLoading}>
            <ErrorBoundary fallback={<h2 className="l-error l-center">Could not fetch favorites data</h2>}>
              {favoritesData && favoritesData.data && favoritesData.data.length > 0 ? (
                <>
                  {favoritesData.data.map((match) => {
                    return userTypeToFetch === 'customers' ? (
                      <ResultsItemCustomer
                        key={match.id}
                        personId={match.id}
                        interval={match.interval}
                        duration={match.duration}
                        days={match.days}
                        score={match.score}
                        location={match.location}
                        locationId={match.location.place_id}
                        experience={match.experience}
                        firstName={match.first_name}
                        profilePicture={stringToJSON(match.profile_picture)}
                        verification={isVerified(match.verification)}
                        lastLogin={match.last_login}
                        isOnline={match.is_online}
                        personalNote={match.personal_note}
                        registerDate={match.registered_date}
                        slug={match.slug}
                        published={match.published}
                        showFavorite
                      />
                    ) : (
                      <ResultsItemWorker
                        key={match.id}
                        personId={match.id}
                        score={match.score}
                        price={match.price}
                        address={match.location}
                        locationId={match.location.place_id}
                        experience={match.experience}
                        firstName={match.first_name}
                        profilePicture={stringToJSON(match.profile_picture)}
                        verification={isVerified(match.verification)}
                        lastLogin={match.last_login}
                        isOnline={match.is_online}
                        personalNote={match.personal_note}
                        registerDate={match.registered_date}
                        slug={match.slug}
                        published={match.published}
                        showFavorite
                      />
                    );
                  })}
                </>
              ) : (
                <NoResults
                  accountType={user.accountType}
                  locationId={
                    user.accountType === 'worker' ? user.operations?.address.place_id : user.wish?.address.place_id
                  }
                />
              )}
            </ErrorBoundary>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default Favorites;
